"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "La même ligne de lecture, six déclarations, trois résultats",
        declaration: "Déclaration",
        ligne: "Ligne où l'on lit",
        mot: "ligne",
        console: "Console",
        zonePret: "déjà utilisable, corps compris",
        zoneUndef: "existe, vaut undefined",
        zoneMorte: "zone morte temporelle",
        zoneApres: "après la déclaration, lecture normale",
        legende: "À la ligne {n}, ce que donnent les six déclarations",
        catValeur: "valeur",
        catUndef: "undefined",
        catErreur: "ReferenceError",
        fonction:
            "Le corps de la fonction est enregistré avant l'exécution de la première ligne. L'appeler plus haut fonctionne, quelle que soit la ligne choisie, et c'est la seule déclaration du lot qui le permette.",
        varAvant:
            "Le nom existe déjà, mais il ne porte encore rien. Aucune erreur, aucun avertissement : la lecture rend undefined et la panne se déclarera bien plus loin, dans la ligne qui exploite cette valeur.",
        varApres:
            "Après sa ligne d'affectation, var se lit comme n'importe quelle variable. C'est au-dessus qu'il pose problème : remontez la lecture pour voir la différence.",
        tdzAvant:
            "Le nom existe, mais il reste inaccessible jusqu'à sa ligne de déclaration. Cet intervalle porte un nom, la zone morte temporelle. Plutôt que de rendre une valeur trompeuse, le langage refuse la lecture et l'erreur pointe la ligne fautive.",
        classeAvant:
            "Une classe ne remonte pas comme une fonction : sa déclaration suit la règle de let. Le nom est réservé dès le début du bloc, et l'instancier avant sa ligne échoue exactement comme une lecture anticipée.",
        flecheeAvant:
            "Le mot function n'apparaît pas ici : ce qui est déclaré, c'est la constante. Elle suit donc la règle de const, pas celle des fonctions. Même nom qu'à la première déclaration, même ligne de lecture, résultat opposé.",
        apres:
            "Après sa ligne de déclaration, la variable se lit normalement. Déclarer avant d'utiliser rend le hoisting invisible, ce qui reste le meilleur état possible.",
        note:
            "Les vingt-quatre états de cette démo, six déclarations par quatre lignes de lecture, ont été exécutés tels quels sous Node : les sorties et les messages d'erreur affichés ici sont les siens, en anglais comme il les produit. Les lignes // ... n'ont aucun effet, elles marquent seulement les emplacements où la lecture peut se poser.",
    },
    en: {
        title: "One read line, six declarations, three outcomes",
        declaration: "Declaration",
        ligne: "Line where the read sits",
        mot: "line",
        console: "Console",
        zonePret: "already callable, body included",
        zoneUndef: "exists, holds undefined",
        zoneMorte: "temporal dead zone",
        zoneApres: "past the declaration, normal read",
        legende: "At line {n}, what the six declarations give",
        catValeur: "value",
        catUndef: "undefined",
        catErreur: "ReferenceError",
        fonction:
            "The function body is registered before the first line runs. Calling it higher up works, whichever line you pick, and it is the only declaration here that allows it.",
        varAvant:
            "The name already exists, but it holds nothing yet. No error, no warning: the read returns undefined and the breakage surfaces much later, in the line that consumes that value.",
        varApres:
            "Past its assignment line, var reads like any other variable. It is above that it causes trouble: move the read up to see the difference.",
        tdzAvant:
            "The name exists, but it stays out of reach until its declaration line. That interval has a name, the temporal dead zone. Rather than hand back a misleading value, the language refuses the read and the error points at the offending line.",
        classeAvant:
            "A class does not hoist the way a function does: its declaration follows the let rule. The name is reserved from the start of the block, and instantiating it before its line fails exactly like any premature read.",
        flecheeAvant:
            "The word function does not appear here: what gets declared is the constant. It therefore follows the const rule, not the function one. Same name as the first declaration, same read line, opposite outcome.",
        apres:
            "Past its declaration line, the variable reads normally. Declaring before using makes hoisting invisible, which remains the best possible state.",
        note:
            "The twenty-four states of this demo, six declarations by four read lines, were each run as they stand under Node: the outputs and error messages shown here are its own. The // ... lines do nothing, they only mark the spots where the read can sit.",
    },
};

// Message exact de V8, verifie sous Node sur les huit etats qui levent.
const refError = (nom) => `ReferenceError: Cannot access '${nom}' before initialization`;

/**
 * Les six formes de declaration, avec la sortie reelle de Node de part et
 * d'autre de la ligne de declaration. Chacun des vingt-quatre etats a ete
 * execute tel qu'il s'affiche ici avant d'etre ecrit dans cette table.
 */
function construireFormes(lang) {
    const nomFn = lang === "fr" ? "direBonjour" : "sayHello";
    const salut = lang === "fr" ? "bonjour" : "hello";
    const lireFn = `console.log(${nomFn}());`;
    const lireTotal = "console.log(total);";
    const chipFlechee = lang === "fr" ? "const nom = () =>" : "const name = () =>";

    return [
        {
            cle: "fonction",
            chip: "function",
            decl: `function ${nomFn}() { return "${salut}"; }`,
            lire: lireFn,
            zone: "pret",
            avant: { etat: "valeur", sortie: salut },
            apres: { etat: "valeur", sortie: salut },
        },
        {
            cle: "var",
            chip: "var",
            decl: "var total = 3;",
            lire: lireTotal,
            zone: "undef",
            avant: { etat: "undefined", sortie: "undefined" },
            apres: { etat: "valeur", sortie: "3" },
        },
        {
            cle: "let",
            chip: "let",
            decl: "let total = 3;",
            lire: lireTotal,
            zone: "morte",
            avant: { etat: "erreur", sortie: refError("total") },
            apres: { etat: "valeur", sortie: "3" },
        },
        {
            cle: "const",
            chip: "const",
            decl: "const total = 3;",
            lire: lireTotal,
            zone: "morte",
            avant: { etat: "erreur", sortie: refError("total") },
            apres: { etat: "valeur", sortie: "3" },
        },
        {
            cle: "classe",
            chip: "class",
            decl: "class Total {}",
            lire: "console.log(new Total());",
            zone: "morte",
            avant: { etat: "erreur", sortie: refError("Total") },
            apres: { etat: "valeur", sortie: "Total {}" },
        },
        {
            cle: "flechee",
            chip: chipFlechee,
            decl: `const ${nomFn} = () => "${salut}";`,
            lire: lireFn,
            zone: "morte",
            avant: { etat: "erreur", sortie: refError(nomFn) },
            apres: { etat: "valeur", sortie: salut },
        },
    ];
}

const ZONES = {
    pret: {
        boite: "border-green-500/40 bg-green-500/[0.06]",
        texte: "text-green-700 dark:text-green-400",
    },
    undef: {
        boite: "border-amber-500/40 bg-amber-500/[0.07]",
        texte: "text-amber-600 dark:text-amber-400",
    },
    morte: {
        boite: "border-red-500/40 bg-red-500/[0.06]",
        texte: "text-red-600 dark:text-red-400",
    },
};

const PASTILLES = {
    valeur: "bg-green-500",
    undefined: "bg-amber-500",
    erreur: "bg-red-500",
};

// Les quatre emplacements possibles de la lecture. La declaration occupe
// toujours la ligne 3 : deux emplacements au-dessus, deux en dessous.
const LIGNES = [1, 2, 4, 5];

export default function HoistingTdz({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const formes = construireFormes(lang);

    const [cle, setCle] = useState("let");
    const [position, setPosition] = useState(2);

    const forme = formes.find((f) => f.cle === cle) || formes[0];
    const ligneLue = LIGNES[position - 1];
    const avant = ligneLue < 3;
    const resultat = avant ? forme.avant : forme.apres;

    // La mesure vient de la meme table que la console : a une ligne donnee,
    // combien des six declarations affichent une valeur, undefined, ou levent.
    const etats = formes.map((f) => (avant ? f.avant.etat : f.apres.etat));
    const compte = (e) => etats.filter((x) => x === e).length;
    const mesures = [
        { valeur: compte("valeur"), label: t.catValeur, pastille: PASTILLES.valeur },
        { valeur: compte("undefined"), label: t.catUndef, pastille: PASTILLES.undefined },
        { valeur: compte("erreur"), label: t.catErreur, pastille: PASTILLES.erreur },
    ];

    let verdict = "apres";
    if (forme.cle === "fonction") verdict = "fonction";
    else if (!avant) verdict = forme.cle === "var" ? "varApres" : "apres";
    else if (forme.cle === "var") verdict = "varAvant";
    else if (forme.cle === "classe") verdict = "classeAvant";
    else if (forme.cle === "flechee") verdict = "flecheeAvant";
    else verdict = "tdzAvant";

    const tons = {
        fonction: "good",
        varAvant: "warn",
        varApres: "neutral",
        tdzAvant: "bad",
        classeAvant: "bad",
        flecheeAvant: "bad",
        apres: "good",
    };

    const zone = ZONES[forme.zone];
    const zoneTexte =
        forme.zone === "pret" ? t.zonePret : forme.zone === "undef" ? t.zoneUndef : t.zoneMorte;

    const rangee = (numero, contenu, lu) => (
        <div key={numero} className={`flex items-start gap-3 px-2 py-1 rounded ${lu ? "bg-primary/10" : ""}`}>
            <span className="w-3 shrink-0 text-right text-gray-400 dark:text-grayDark-600 select-none">
                {numero}
            </span>
            <span
                className={`whitespace-pre ${
                    lu
                        ? "text-primary font-bold"
                        : contenu.startsWith("//")
                        ? "text-gray-400 dark:text-grayDark-600"
                        : "text-gray-800 dark:text-white font-bold"
                }`}
            >
                {contenu}
            </span>
        </div>
    );

    const contenuLigne = (numero) => {
        if (numero === 3) return forme.decl;
        return numero === ligneLue ? forme.lire : "// ...";
    };

    return (
        <Frame icon="code-square-linear" title={t.title}>
            <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.declaration}</div>
            <div className="flex flex-wrap gap-2">
                {formes.map((f) => {
                    const actif = f.cle === cle;
                    const etat = avant ? f.avant.etat : f.apres.etat;
                    return (
                        <button
                            key={f.cle}
                            type="button"
                            aria-pressed={actif}
                            onClick={() => setCle(f.cle)}
                            className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 font-geistMono text-[11px] sm:text-xs whitespace-nowrap transition-colors duration-200 ${
                                actif
                                    ? "border-primary bg-primary/10 text-primary font-bold"
                                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-white/10 dark:text-lightBlue dark:hover:border-white/20"
                            }`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PASTILLES[etat]}`} />
                            {f.chip}
                        </button>
                    );
                })}
            </div>

            <div className="mt-5">
                <Slider
                    label={t.ligne}
                    value={position}
                    onChange={setPosition}
                    min={1}
                    max={4}
                    step={1}
                    display={`${t.mot} ${ligneLue}`}
                />
            </div>

            <div className="mt-5 rounded-xl border border-gray-200/70 dark:border-white/10 bg-gray-50 dark:bg-white/[0.04] p-3 font-geistMono text-xs sm:text-sm overflow-x-auto">
                <div className="min-w-max">
                    <div className={`rounded-lg border-l-2 ${zone.boite} py-1 pr-1`}>
                        <div
                            className={`px-2 pb-0.5 text-[10px] uppercase tracking-widest ${zone.texte}`}
                        >
                            {zoneTexte}
                        </div>
                        {rangee(1, contenuLigne(1), ligneLue === 1)}
                        {rangee(2, contenuLigne(2), ligneLue === 2)}
                    </div>
                    <div className="border-l-2 border-transparent pr-1 py-1">
                        {rangee(3, contenuLigne(3), false)}
                    </div>
                    <div className={`rounded-lg border-l-2 ${ZONES.pret.boite} py-1 pr-1`}>
                        <div
                            className={`px-2 pb-0.5 text-[10px] uppercase tracking-widest ${ZONES.pret.texte}`}
                        >
                            {t.zoneApres}
                        </div>
                        {rangee(4, contenuLigne(4), ligneLue === 4)}
                        {rangee(5, contenuLigne(5), ligneLue === 5)}
                    </div>
                </div>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200/70 dark:border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-500 mb-1.5">
                    {t.console}
                </div>
                <div
                    className={`flex gap-2 font-geistMono text-xs sm:text-sm break-words ${
                        resultat.etat === "erreur"
                            ? "text-red-600 dark:text-red-400"
                            : resultat.etat === "undefined"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-800 dark:text-white"
                    }`}
                >
                    <span className="text-gray-400 dark:text-grayDark-600 shrink-0">
                        {resultat.etat === "erreur" ? "✗" : "›"}
                    </span>
                    <span className="min-w-0 font-bold">{resultat.sortie}</span>
                </div>
            </div>

            <div className="mt-4">
                <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-2">
                    {t.legende.replace("{n}", String(ligneLue))}
                </div>
                <div className="rounded-xl border border-gray-200/70 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/10">
                    {mesures.map((m) => (
                        <div
                            key={m.label}
                            className={`flex items-center gap-2 px-3 py-2 ${m.valeur === 0 ? "opacity-40" : ""}`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.pastille}`} />
                            <span className="flex-1 font-geistMono text-[11px] sm:text-xs text-gray-600 dark:text-lightBlue">
                                {m.label}
                            </span>
                            <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                                {m.valeur}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <Verdict tone={tons[verdict]}>{t[verdict]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
